<template>
  <div>
    <b-row>
      <b-col>
        <b-card class="header-marcar_consulta bg-gradient-primary">
          <b-row align-v="center">
            <b-col lg="6">
              <h1>
                <feather-icon icon="AwardIcon" size="30" class="mr-50" />
                {{ isEdit ? 'Editar' : 'Adicionar' }} Pontos Manuais
              </h1>
            </b-col>
            <b-col lg="6" class="text-right">
              <b-button variant="secondary" @click="$router.go(-1)">
                <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
                Voltar
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <b-card>
      <b-form @submit.prevent="submitForm">
        <b-row>
          <b-col md="6">
            <b-form-group label="Escola *" label-for="escola">
              <v-select
                id="escola"
                v-model="form.escola"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="schoolOptions"
                value="id"
                label="name"
                placeholder="Selecione uma escola"
                @input="onSchoolChange"
                :disabled="isEdit"
              />
              <small v-if="errors.escola" class="text-danger">{{ errors.escola }}</small>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Turma *" label-for="turma">
              <v-select
                id="turma"
                v-model="form.turma"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="classroomOptions"
                value="id"
                label="name"
                placeholder="Selecione uma turma"
                :disabled="!form.escola || isEdit"
                @input="onClassroomChange"
              />
              <small v-if="errors.turma" class="text-danger">{{ errors.turma }}</small>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Alunos *" label-for="alunos">
              <v-select
                id="alunos"
                v-model="form.alunos"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="studentOptions"
                value="id"
                label="fullName"
                multiple
                :closeOnSelect="false"
                placeholder="Selecione os alunos"
                :disabled="!form.turma"
                class="mb-2"
              />
              <div class="d-flex align-items-center flex-wrap gap-2">
                <b-button
                  v-if="form.turma && studentOptions.length > 0"
                  variant="outline-primary"
                  size="sm"
                  @click="selectAllStudents"
                  :disabled="form.alunos.length === studentOptions.length"
                >
                  <feather-icon icon="CheckSquareIcon" size="14" class="mr-50" />
                  Selecionar Todos da Turma
                </b-button>
                <b-button
                  v-if="form.alunos.length > 0"
                  variant="outline-secondary"
                  size="sm"
                  @click="clearAllStudents"
                >
                  <feather-icon icon="XIcon" size="14" class="mr-50" />
                  Limpar
                </b-button>
              </div>
              <small v-if="errors.alunos" class="text-danger">{{ errors.alunos }}</small>
              <small class="text-muted">
                Selecione um ou mais alunos 
                <span v-if="form.alunos.length > 0">
                  ({{ form.alunos.length }} selecionado{{ form.alunos.length > 1 ? 's' : '' }})
                </span>
              </small>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Desafio *" label-for="motivo">
              <v-select
                id="motivo"
                v-model="form.motivo"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="motivoOptions"
                placeholder="Selecione um desafio"
              />
              <small v-if="errors.motivo" class="text-danger">{{ errors.motivo }}</small>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Pontuação *" label-for="pontuacao">
              <b-form-input
                id="pontuacao"
                v-model.number="form.pontuacao"
                type="number"
                min="0"
                max="10"
                step="0.1"
                placeholder="Digite a pontuação (0 a 10)"
              />
              <small v-if="errors.pontuacao" class="text-danger">{{ errors.pontuacao }}</small>
              <small class="text-muted">Pontuação de 0 a 10</small>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col class="text-right">
            <b-button variant="secondary" @click="$router.go(-1)" class="mr-2">
              Cancelar
            </b-button>
            <b-button variant="primary" type="submit" :disabled="isSubmitting">
              <b-spinner v-if="isSubmitting" small class="mr-1"></b-spinner>
              {{ isSubmitting ? 'Salvando...' : 'Salvar' }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BSpinner,
} from 'bootstrap-vue'
import { hideSpinner, showSpinner } from '@/services/spinService'
import pontosManuaisService from '@/services/pontosManuaisService'
import schoolService from '@/services/schoolService'
import classroomService from '@/services/classroomService'
import usersService from '@/services/usersService'
import { RoleEnum } from '@/utils/constants'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BButton,
    BSpinner,
    vSelect,
    ToastificationContent,
  },
  data() {
    return {
      isEdit: false,
      isSubmitting: false,
      form: {
        id: null,
        escola: null,
        turma: null,
        alunos: [],
        motivo: null,
        pontuacao: null,
      },
      errors: {},
      schoolOptions: [],
      classroomOptions: [],
      studentOptions: [],
      motivoOptions: [
        { value: 'Coleta e Reciclagem', label: 'Coleta e Reciclagem' },
        { value: 'Arboturma', label: 'Arboturma' },
      ],
    }
  },
  async mounted() {
    await this.loadSchools()
    
    if (this.$route.params.id) {
      this.isEdit = true
      await this.loadPonto()
    }
  },
  methods: {
    async loadSchools() {
      try {
        const filtro = {
          name: '',
          page: 1,
          itemsPerPage: 99,
        }
        const response = await schoolService.getAll(filtro)
        this.schoolOptions = response.content || []
      } catch (error) {
        console.error('Erro ao carregar escolas:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar escolas',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    async onSchoolChange(school) {
      this.form.turma = null
      this.form.alunos = []
      this.classroomOptions = []
      this.studentOptions = []
      
      if (!school) return

      try {
        const filtro = {
          name: '',
          page: 1,
          itemsPerPage: 99,
        }
        const response = await classroomService.getAll(filtro)

        // Filtra turmas da escola selecionada
        this.classroomOptions = (response.content || [])
          .filter(classroom => classroom.escola === school.name)
          .map(classroom => ({
            ...classroom,
            name: classroom.ano + ' - ' + classroom.turma + ' ' + classroom.turno
          }))
          .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      } catch (error) {
        console.error('Erro ao carregar turmas:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar turmas',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    async onClassroomChange(classroom) {
      this.form.alunos = []
      this.studentOptions = []
      
      if (!classroom) return

      try {
        showSpinner()
        const filtro = {
          role: RoleEnum.Student,
          page: 1,
          itemsPerPage: 99,
        }
        const response = await usersService.getAll(filtro)

        // Filtra alunos da turma selecionada
        // Compara classroom (nome da turma) e turno
        this.studentOptions = (response.content || [])
          .filter(student => {
            const studentClassroom = student.turmaFormatada.replaceAll(' ', '').replaceAll('-', '').toLowerCase()
            const classroomName = classroom.name.replaceAll(' ', '').replaceAll('-', '').toLowerCase()
            return studentClassroom === classroomName
          })
        hideSpinner()
      } catch (error) {
        hideSpinner()
        console.error('Erro ao carregar alunos:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar alunos',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    selectAllStudents() {
      this.form.alunos = [...this.studentOptions]
    },

    clearAllStudents() {
      this.form.alunos = []
    },

    async loadPonto() {
      try {
        showSpinner()
        const ponto = await pontosManuaisService.getById(this.$route.params.id)
        
        // Carrega escola
        const escola = this.schoolOptions.find(s => s.name === ponto.escola)
        if (escola) {
          this.form.escola = escola
          await this.onSchoolChange(escola)
          
          // Carrega turma
          const turma = this.classroomOptions.find(t => 
            t.turma === ponto.turma && t.escola === ponto.escola
          )
          if (turma) {
            this.form.turma = turma
            await this.onClassroomChange(turma)
            
            // Carrega alunos
            if (ponto.alunosIds && Array.isArray(ponto.alunosIds)) {
              this.form.alunos = this.studentOptions.filter(s => 
                ponto.alunosIds.includes(s.id)
              )
            }
          }
        }
        
        this.form.motivo = this.motivoOptions.find(m => m.value === ponto.motivo)
        this.form.pontuacao = ponto.pontuacao || null
        this.form.id = ponto.id
        
        hideSpinner()
      } catch (error) {
        hideSpinner()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar ponto manual',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        this.$router.go(-1)
      }
    },

    validateForm() {
      this.errors = {}
      let isValid = true

      if (!this.form.escola) {
        this.errors.escola = 'Selecione uma escola'
        isValid = false
      }

      if (!this.form.turma) {
        this.errors.turma = 'Selecione uma turma'
        isValid = false
      }

      if (!this.form.alunos || this.form.alunos.length === 0) {
        this.errors.alunos = 'Selecione pelo menos um aluno'
        isValid = false
      }

      if (!this.form.motivo) {
        this.errors.motivo = 'Selecione um desafio'
        isValid = false
      }

      if (this.form.pontuacao === null || this.form.pontuacao === undefined || this.form.pontuacao === '') {
        this.errors.pontuacao = 'Informe a pontuação'
        isValid = false
      } else if (this.form.pontuacao < 0 || this.form.pontuacao > 10) {
        this.errors.pontuacao = 'A pontuação deve estar entre 0 e 10'
        isValid = false
      }

      return isValid
    },

    async submitForm() {
      if (!this.validateForm()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Preencha todos os campos obrigatórios',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      this.isSubmitting = true
      showSpinner()

      try {
        const payload = {
          escolaId: this.form.escola.id,
          escola: this.form.escola.name,
          turmaId: this.form.turma.id,
          turma: this.form.turma.turma,
          alunosIds: this.form.alunos.map(a => a.id),
          motivo: this.form.motivo.value,
          pontuacao: parseFloat(this.form.pontuacao),
        }

        if (this.isEdit) {
          payload.id = this.form.id
          await pontosManuaisService.update(payload)
        } else {
          await pontosManuaisService.create(payload)
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: `Ponto manual ${this.isEdit ? 'atualizado' : 'criado'} com sucesso!`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        this.$router.push({ name: 'PontosManuaisLista' })
      } catch (error) {
        console.error('Erro ao salvar ponto manual:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: `Erro ao ${this.isEdit ? 'atualizar' : 'criar'} ponto manual`,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isSubmitting = false
        hideSpinner()
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

